// Admin dashboard — black/ivory/gold luxury aesthetic.
// Password-protected against backend ADMIN_LEADS_PASSWORD env var.
// Click a row to open the detail drawer where status/notes/follow-up can be edited.
import { useEffect, useMemo, useState } from "react";
import {
  Download,
  Search,
  Filter,
  LogOut,
  Mail,
  Building2,
  Globe,
  X,
  Calendar,
  ExternalLink,
} from "lucide-react";
import { LOGO, TAGLINE } from "@/lib/sections";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "aarya24kt_admin_token";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return iso;
  }
};

const todayISO = () => new Date().toISOString().slice(0, 10);

// Tell crawlers not to index any admin page (defence-in-depth alongside the password)
function useNoIndex() {
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow, noarchive";
    document.head.appendChild(tag);
    return () => {
      tag.parentNode && tag.parentNode.removeChild(tag);
    };
  }, []);
}

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        setError("Invalid password.");
        setSubmitting(false);
        return;
      }
      sessionStorage.setItem(STORAGE_KEY, password);
      onLogin(password);
    } catch {
      setError("Network error. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <img
          src={LOGO.mark}
          alt="Aarya24KT"
          className="h-20 w-auto mx-auto mb-1 object-contain"
          crossOrigin="anonymous"
        />
        <div className="text-center font-heading italic text-gold/85 text-sm md:text-base tracking-wide">
          {TAGLINE}
        </div>
        <div className="mt-12 border border-gold/25 bg-coal/60 p-8 md:p-10">
          <div className="num-overline text-gold/80">Lead Dashboard</div>
          <h1 className="mt-3 font-heading text-ivory text-3xl">
            Sign <span className="italic text-gold">in.</span>
          </h1>
          <p className="mt-3 font-body text-[13px] text-ivory/55 leading-relaxed">
            Restricted access — Aarya24KT global partner enquiries.
          </p>
          <form onSubmit={submit} className="mt-7" data-testid="admin-login-form">
            <label className="block num-overline text-ivory/55 text-[10px] mb-2">Password</label>
            <input
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none text-ivory py-3 font-body text-[15px] tracking-wide"
            />
            {error && (
              <div className="mt-3 text-[12px] text-red-300/80" data-testid="admin-login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting || !password}
              data-testid="admin-login-submit"
              className="mt-8 w-full bg-gold text-ink py-3.5 font-body text-[11px] uppercase tracking-[0.25em] hover:bg-ivory transition-colors duration-500 ease-luxe disabled:opacity-50"
            >
              {submitting ? "Verifying…" : "Enter"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div
      data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`border ${accent ? "border-gold/40 bg-gold/[0.04]" : "border-ivory/12"} px-5 py-4 md:py-5`}
    >
      <div className="num-overline text-[9.5px] text-ivory/55">{label}</div>
      <div className={`mt-1.5 font-heading text-3xl md:text-4xl ${accent ? "text-gold" : "text-ivory"}`}>
        {value}
      </div>
    </div>
  );
}

function FilterPill({ label, count, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left border px-4 py-2.5 transition-colors duration-300 ease-luxe flex items-baseline justify-between gap-3 ${
        active
          ? "border-gold bg-gold/10 text-ivory"
          : "border-ivory/12 text-ivory/75 hover:border-gold/60"
      }`}
    >
      <span className="font-body text-[12.5px]">{label}</span>
      <span className="num-overline text-[10px] text-ivory/45">{count}</span>
    </button>
  );
}

function LeadRow({ lead, label, statusLabel, onSelect }) {
  return (
    <button
      onClick={() => onSelect(lead)}
      data-testid={`lead-row-${lead.id}`}
      className="text-left w-full grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 py-5 md:py-6 border-b border-ivory/8 hover:bg-gold/[0.025] transition-colors duration-300"
    >
      <div className="md:col-span-3">
        <div className="flex items-center gap-2">
          <div className="num-overline text-gold/85 text-[10px]">{label}</div>
          {lead.status && lead.status !== "new" && (
            <span className="num-overline text-[9px] text-ivory/45 border border-ivory/15 px-2 py-[2px]">
              {statusLabel}
            </span>
          )}
        </div>
        <div className="mt-1.5 font-body text-[12px] text-ivory/45">{fmtDate(lead.timestamp)}</div>
      </div>
      <div className="md:col-span-4">
        <div className="font-heading text-xl md:text-[22px] text-ivory">{lead.name}</div>
        <div className="mt-1 inline-flex items-center gap-1.5 font-body text-[13px] text-gold/85">
          <Mail className="h-3 w-3" />
          {lead.email}
        </div>
      </div>
      <div className="md:col-span-3 font-body text-[13px] text-ivory/75 space-y-1.5">
        {lead.company && (
          <div className="flex items-start gap-2">
            <Building2 className="h-3.5 w-3.5 mt-1 text-ivory/40 shrink-0" />
            <span>{lead.company}</span>
          </div>
        )}
        {lead.country && (
          <div className="flex items-start gap-2">
            <Globe className="h-3.5 w-3.5 mt-1 text-ivory/40 shrink-0" />
            <span>{lead.country}</span>
          </div>
        )}
      </div>
      <div className="md:col-span-2 font-body text-[12px] text-ivory/55 leading-relaxed space-y-1.5">
        {lead.notes && (
          <div className="line-clamp-2">
            <span className="num-overline text-[9px] text-gold/65 block">Notes</span>
            {lead.notes}
          </div>
        )}
        {lead.follow_up_date && (
          <div className="flex items-center gap-1.5 text-gold/75">
            <Calendar className="h-3 w-3" />
            {lead.follow_up_date}
          </div>
        )}
      </div>
    </button>
  );
}

function LeadDetail({ lead, statuses, statusLabels, ctaLabels, token, onClose, onSaved }) {
  const [status, setStatus] = useState(lead.status || "new");
  const [notes, setNotes] = useState(lead.notes || "");
  const [followUp, setFollowUp] = useState(lead.follow_up_date || "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/leads/${lead.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status, notes, follow_up_date: followUp }),
      });
      if (res.ok) {
        const updated = await res.json();
        setSavedAt(new Date());
        onSaved(updated);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="flex-1 bg-ink/85 backdrop-blur-sm"
        onClick={onClose}
        data-testid="detail-backdrop"
      />
      {/* Drawer */}
      <aside
        data-testid="lead-detail-drawer"
        className="w-full max-w-[560px] bg-coal border-l border-gold/20 overflow-y-auto"
      >
        <div className="sticky top-0 bg-coal/95 backdrop-blur-xl border-b border-ivory/8 px-6 md:px-8 py-5 flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="num-overline text-gold/80 text-[10px]">
              {ctaLabels[lead.cta_source] || lead.cta_source}
            </div>
            <h2 className="mt-1.5 font-heading text-ivory text-2xl md:text-3xl leading-tight truncate">
              {lead.name}
            </h2>
            <div className="mt-1 font-body text-[12px] text-ivory/45">{fmtDate(lead.timestamp)}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="detail-close-btn"
            aria-label="Close"
            className="h-10 w-10 inline-flex items-center justify-center border border-ivory/15 text-ivory/70 hover:border-gold hover:text-gold transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 md:px-8 py-8 space-y-8">
          {/* Read-only details */}
          <div className="space-y-3 font-body text-[14px]">
            <DetailRow label="Email">
              <a href={`mailto:${lead.email}`} className="text-gold hover:underline">
                {lead.email}
              </a>
            </DetailRow>
            <DetailRow label="Company">{lead.company || "—"}</DetailRow>
            <DetailRow label="Country">{lead.country || "—"}</DetailRow>
            {lead.shipping_address && (
              <DetailRow label="Shipping">{lead.shipping_address}</DetailRow>
            )}
            {lead.message && <DetailRow label="Message">{lead.message}</DetailRow>}
            {lead.source_url && (
              <DetailRow label="Source">
                <a
                  href={lead.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-ivory/75 hover:text-gold transition-colors break-all"
                >
                  <span>{lead.source_url}</span>
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
              </DetailRow>
            )}
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />

          {/* Editable CRM block */}
          <div className="space-y-6">
            <div>
              <label className="block num-overline text-[10px] text-ivory/55 mb-2.5">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="detail-status-select"
                className="w-full bg-transparent border border-ivory/15 focus:border-gold outline-none py-3 px-4 font-body text-[14px] text-ivory appearance-none"
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 12 8' xmlns='http://www.w3.org/2000/svg' fill='none' stroke='%23C8A46A' stroke-width='1.5'%3E%3Cpath d='M1 1l5 5 5-5'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 1rem center", backgroundSize: "12px" }}
              >
                {statuses.map((s) => (
                  <option key={s} value={s} className="bg-coal">
                    {statusLabels[s] || s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block num-overline text-[10px] text-ivory/55 mb-2.5">
                Internal notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Conversation log, preferences, next steps…"
                rows={5}
                data-testid="detail-notes-input"
                className="w-full bg-transparent border border-ivory/15 focus:border-gold outline-none py-3 px-4 font-body text-[14px] text-ivory placeholder:text-ivory/30 leading-relaxed resize-none"
              />
            </div>

            <div>
              <label className="block num-overline text-[10px] text-ivory/55 mb-2.5">
                Follow-up date
              </label>
              <input
                type="date"
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
                data-testid="detail-followup-input"
                className="w-full bg-transparent border border-ivory/15 focus:border-gold outline-none py-3 px-4 font-body text-[14px] text-ivory"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={save}
                disabled={saving}
                data-testid="detail-save-btn"
                className="bg-gold text-ink px-7 py-3.5 font-body text-[11px] uppercase tracking-[0.25em] hover:bg-ivory transition-colors duration-500 ease-luxe disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              {savedAt && (
                <span className="num-overline text-[10px] text-gold/70">Saved · just now</span>
              )}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

const DetailRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 items-start">
    <div className="col-span-4 num-overline text-[10px] text-ivory/45 mt-0.5">{label}</div>
    <div className="col-span-8 text-ivory/85 leading-relaxed break-words">{children}</div>
  </div>
);

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState({
    leads: [],
    total: 0,
    summary: { total: 0, new: 0, catalog: 0, samples: 0, converted: 0 },
    by_cta_source: {},
    by_status: {},
    cta_labels: {},
    status_labels: {},
    statuses: [],
  });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({
    cta_source: "all",
    status: "all",
    country: "",
    date_from: "",
    date_to: "",
    search: "",
  });

  const fetchLeads = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "all") params.set(k, v);
    });
    try {
      const res = await fetch(`${API}/admin/leads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        onLogout();
        return;
      }
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error("Failed to fetch leads:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const id = setTimeout(fetchLeads, filters.search ? 350 : 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const exportCsv = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "all") params.set(k, v);
    });
    fetch(`${API}/admin/leads/export.csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `aarya24kt-leads-${todayISO()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  const onLeadSaved = (updated) => {
    setData((d) => ({
      ...d,
      leads: d.leads.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)),
    }));
    setSelected((s) => (s ? { ...s, ...updated } : s));
    // Refresh summary stats in the background
    fetchLeads();
  };

  const ctaOptions = useMemo(() => {
    const labels = data.cta_labels || {};
    const counts = data.by_cta_source || {};
    return [
      { key: "all", label: "All", count: data.total },
      ...Object.entries(labels).map(([k, v]) => ({ key: k, label: v, count: counts[k] || 0 })),
    ];
  }, [data]);

  const statusOptions = useMemo(() => {
    const labels = data.status_labels || {};
    const counts = data.by_status || {};
    return [
      { key: "all", label: "All", count: data.total },
      ...(data.statuses || []).map((s) => ({
        key: s,
        label: labels[s] || s,
        count: counts[s] || 0,
      })),
    ];
  }, [data]);

  return (
    <div className="min-h-screen bg-ink text-ivory">
      {/* Header */}
      <header className="border-b border-gold/15 bg-ink/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto max-w-[1600px] flex items-center justify-between px-6 md:px-12 py-4 md:py-5">
          <a href="/" className="flex items-center gap-4">
            <img
              src={LOGO.mark}
              alt="Aarya24KT"
              className="h-10 md:h-12 w-auto object-contain"
              crossOrigin="anonymous"
            />
            <span className="hidden md:inline font-heading italic text-gold/80 text-sm border-l border-gold/25 pl-4">
              Lead Dashboard
            </span>
          </a>
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={exportCsv}
              data-testid="export-csv-btn"
              className="inline-flex items-center gap-2 bg-gold text-ink px-4 md:px-5 py-2.5 text-[10.5px] md:text-[11px] uppercase tracking-[0.22em] hover:bg-ivory transition-colors duration-500 ease-luxe"
            >
              <Download className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">CSV</span>
            </button>
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              aria-label="Sign out"
              className="inline-flex items-center justify-center h-11 w-11 border border-ivory/15 text-ivory/70 hover:border-gold hover:text-gold transition-colors duration-500 ease-luxe"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-6 md:px-12 py-8 md:py-12">
        {/* Summary tiles */}
        <section className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
          <StatTile label="Total Leads" value={data.summary.total} accent />
          <StatTile label="New" value={data.summary.new} />
          <StatTile label="Catalog" value={data.summary.catalog} />
          <StatTile label="Samples" value={data.summary.samples} />
          <StatTile label="Converted" value={data.summary.converted} />
        </section>

        {/* Lead-type filter pills */}
        <section className="mt-10">
          <div className="num-overline text-[10px] text-ivory/50 mb-3">Lead type</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {ctaOptions.map((o) => (
              <FilterPill
                key={o.key}
                label={o.label}
                count={o.count}
                active={filters.cta_source === o.key}
                onClick={() => setFilters((f) => ({ ...f, cta_source: o.key }))}
                testid={`filter-cta-${o.key}`}
              />
            ))}
          </div>
        </section>

        {/* Status filter pills */}
        <section className="mt-6">
          <div className="num-overline text-[10px] text-ivory/50 mb-3">Status</div>
          <div className="flex flex-wrap gap-2.5">
            {statusOptions.map((o) => (
              <FilterPill
                key={o.key}
                label={o.label}
                count={o.count}
                active={filters.status === o.key}
                onClick={() => setFilters((f) => ({ ...f, status: o.key }))}
                testid={`filter-status-${o.key}`}
              />
            ))}
          </div>
        </section>

        {/* Search / country / date */}
        <section className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block num-overline text-[10px] text-ivory/50 mb-2">
              <Search className="inline h-3 w-3 mr-1.5 -mt-px" />
              Search
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="name, email or company"
              data-testid="filter-search"
              className="w-full bg-transparent border-b border-ivory/20 focus:border-gold outline-none py-2.5 font-body text-[14px] text-ivory placeholder:text-ivory/30"
            />
          </div>
          <div>
            <label className="block num-overline text-[10px] text-ivory/50 mb-2">
              <Filter className="inline h-3 w-3 mr-1.5 -mt-px" />
              Country
            </label>
            <input
              type="text"
              value={filters.country}
              onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
              placeholder="exact match"
              data-testid="filter-country"
              className="w-full bg-transparent border-b border-ivory/20 focus:border-gold outline-none py-2.5 font-body text-[14px] text-ivory placeholder:text-ivory/30"
            />
          </div>
          <div>
            <label className="block num-overline text-[10px] text-ivory/50 mb-2">From</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
              data-testid="filter-date-from"
              className="w-full bg-transparent border-b border-ivory/20 focus:border-gold outline-none py-2.5 font-body text-[14px] text-ivory"
            />
          </div>
          <div>
            <label className="block num-overline text-[10px] text-ivory/50 mb-2">To</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
              data-testid="filter-date-to"
              className="w-full bg-transparent border-b border-ivory/20 focus:border-gold outline-none py-2.5 font-body text-[14px] text-ivory"
            />
          </div>
        </section>

        {/* Leads list */}
        <section className="mt-10">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-heading italic text-gold text-lg">
              Enquiries
              <span className="ml-3 num-overline text-ivory/40 text-[10px] not-italic">
                {loading ? "Loading…" : `${data.leads.length} of ${data.total}`}
              </span>
            </h2>
          </div>

          {!loading && data.leads.length === 0 && (
            <div
              className="border border-ivory/10 py-20 text-center font-body text-ivory/40"
              data-testid="no-leads"
            >
              No leads match the current filters.
            </div>
          )}

          <div className="border-t border-ivory/8" data-testid="leads-list">
            {data.leads.map((lead) => (
              <LeadRow
                key={lead.id}
                lead={lead}
                label={data.cta_labels[lead.cta_source] || lead.cta_source}
                statusLabel={data.status_labels[lead.status] || lead.status}
                onSelect={setSelected}
              />
            ))}
          </div>
        </section>
      </main>

      {selected && (
        <LeadDetail
          lead={selected}
          statuses={data.statuses}
          statusLabels={data.status_labels}
          ctaLabels={data.cta_labels}
          token={token}
          onClose={() => setSelected(null)}
          onSaved={onLeadSaved}
        />
      )}
    </div>
  );
}

export default function AdminLeads() {
  useNoIndex();
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY));

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken(null);
  };

  if (!token) return <LoginScreen onLogin={setToken} />;
  return <Dashboard token={token} onLogout={handleLogout} />;
}
